import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';

import { Modal, PresetNameForm } from '@components/common';
import Confirm from '@components/common/Confirm';
import Select from '@components/common/Select';
import PresetForm from '@components/Kpi/Preset/PresetForm';

import { IEditPresetNameDTO, IPresetDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IPresetTypesProps } from './Preset.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EPresetsGroupNames } from '@enums/kpi-common';

import { HALF_YEAR_DATE_INTERVAL } from '@constants/KPI/kpi-common';

import './Preset.scss';

const PRESET_FORM_WIDTH = '502px';

const Preset = ({
	deletePreset,
	togglePresetNameForm,
	setRenamablePreset,
	setPresetValue,
	setStateSelectedPreset,
	isOpenRenamePresetForm,
	closePresetNameForm,
	presetSaveDate,
	renamablePreset,
	updatePresetName,
	createPreset,
	isLoadingPresetForm,
	presets,
	presetValue,
	isMap,
	minHeight,
	propsPresetForm,
	isEditable = false,
	isPresetModifiedByUser = false,
	maxDateInterval = HALF_YEAR_DATE_INTERVAL,
	clearHandler,
	isOranVendor = false,
}: IPresetTypesProps) => {
	const [isOpenDeleteConfirmPreset, setIsOpenDeleteConfirmPreset] = useState(false);
	const [idForDeletePreset, setIdForDeletePreset] = useState<string>('');
	const [nameForDeletePreset, setNameForDeletePreset] = useState<string>('');
	const [isDirtyPresetNameForm, setIsDirtyPresetNameForm] = useState(false);
	const [isOpenPresetForm, setIsOpenPresetForm] = useState(false);
	const [isDirtyPresetForm, setIsDirtyPresetForm] = useState(false);

	const [formEditablePreset, setFormEditablePreset] = useState<IPresetDTO | null>(null);

	const openDeleteConfirmPreset = (data: TSelectOptionType) => {
		setIdForDeletePreset(data?.id as string);
		if (data?.name) {
			setNameForDeletePreset(data?.name);
		}
		setIsOpenDeleteConfirmPreset(true);
	};

	const closeDeleteConfirmPreset = () => setIsOpenDeleteConfirmPreset(false);

	const handleDeletePreset = () => {
		deletePreset?.({ id: idForDeletePreset, name: nameForDeletePreset });
		setIsOpenDeleteConfirmPreset(false);
		setPresetValue?.(null);
		clearHandler?.(false);
	};

	const openEditPresetNameForm = (editablePreset: IEditPresetNameDTO) => {
		setRenamablePreset?.(editablePreset);
		togglePresetNameForm?.();
	};

	const onRenameClickPreset = (data: TSelectOptionType<string>) => {
		if (data?.name && typeof data?.id === 'string') {
			openEditPresetNameForm({
				name: data?.name,
				id: data?.id,
				isShared: data?.isShared,
			});
		}
	};

	const onEditClickPreset = (option: TSelectOptionType) => {
		const preset: IPresetDTO = {
			id: option?.id as string,
			aggregationType: option?.aggregationType,
			from: option?.from,
			to: option?.to,
			kpiIds: option?.kpis?.map((kpi: { id: string }) => kpi.id),
			sites: option?.sites,
			vendor: option?.vendor.name,
			presetId: (option?.id || '').toString(),
			granularity: option?.granularity,
			isAutomaticGranularity: option?.isAutomaticGranularity,
			dateSelectionMode: option?.dateSelectionMode?.name,
			dynamicDateRange: option?.dynamicDateRange?.name,
			startDateOffset: option?.startDateOffset,
			isShared: option?.isShared,
		};

		setFormEditablePreset(preset);
		setIsOpenPresetForm(true);
	};

	const onChangePreset = (option: TSelectOptionType<string>) => {
		const preset: IPresetDTO = {
			aggregationType: option?.aggregationType,
			from: option?.from,
			to: option?.to,
			kpiIds: option?.kpis?.map((kpi: { id: string }) => kpi.id) || [],
			sites: option?.sites || [],
			vendor: option?.vendor.name,
			presetId: (option?.id || '').toString(),
			granularity: option?.granularity,
			isAutomaticGranularity: option?.isAutomaticGranularity,
			dateSelectionMode: option?.dateSelectionMode?.name,
			dynamicDateRange: option?.dynamicDateRange?.name,
			startDateOffset: option?.startDateOffset,
		};

		setPresetValue?.(option);
		setStateSelectedPreset?.(option ? preset : undefined);
	};

	const closePresetForm = () => {
		setFormEditablePreset(null);
		setIsOpenPresetForm(false);
	};

	const handleCloseRenamePresetForm = () => {
		closePresetNameForm();
		closePresetForm();
	};

	return (
		<div className={cn('f5gKpiPreset', { 'f5gKpiPresetFullWidth': isMap })}>
			<Modal
				isOpen={isOpenDeleteConfirmPreset}
				onClose={closeDeleteConfirmPreset}
			>
				<Confirm
					onClose={closeDeleteConfirmPreset}
					onSubmit={handleDeletePreset}
					question='Are you sure you want to delete preset?'
				/>
			</Modal>
			{createPortal(
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyPresetNameForm}
					isOpen={isOpenRenamePresetForm}
					onClose={handleCloseRenamePresetForm}
					title={presetSaveDate ? 'Save as preset' : 'Rename preset'}
				>
					<PresetNameForm
						closePresetNameForm={handleCloseRenamePresetForm}
						editablePreset={renamablePreset}
						isLoading={isLoadingPresetForm}
						isOranVendor={isOranVendor}
						onEdit={updatePresetName}
						onSave={createPreset}
						presetSaveDate={presetSaveDate}
						presetValue={presetValue}
						setIsDirty={setIsDirtyPresetNameForm}
						setPresetValue={setPresetValue}
					/>
				</Modal>,
				document.body,
			)}
			{createPortal(
				<Modal
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyPresetForm}
					isOpen={!!(isOpenPresetForm && formEditablePreset && propsPresetForm)}
					isScrollableContent
					onClose={closePresetForm}
					title='Edit preset'
					width={PRESET_FORM_WIDTH}
				>
					{isOpenPresetForm && formEditablePreset && propsPresetForm && (
						<PresetForm
							closePresetForm={closePresetForm}
							formEditablePreset={formEditablePreset}
							isDirtyForm={isDirtyPresetForm}
							isLoading={isLoadingPresetForm}
							isLoadingOptions={propsPresetForm.isLoadingOptions}
							maxDateInterval={maxDateInterval}
							openPresetForm={propsPresetForm.openPresetForm}
							options5GOran={propsPresetForm.options5GOran}
							optionsVendorsKpi={propsPresetForm.optionsVendorsKpi}
							presets={presets}
							selectedPresetValue={presetValue}
							setIsDirtyPresetForm={setIsDirtyPresetForm}
							setStateSelectedPreset={setStateSelectedPreset}
							updatePreset={propsPresetForm.updatePreset}
						/>
					)}
				</Modal>,
				document.body,
			)}
			<Select
				disabled={isLoadingPresetForm}
				editTooltip='Edit preset'
				emptyGroupText='No presets'
				handleChange={onChangePreset}
				initialGroupLabels={isOranVendor
					? [EPresetsGroupNames.personal, EPresetsGroupNames.shared]
					: undefined
			}
				isActionsDropdown={isOranVendor}
				isClearable
				isEditable={isEditable}
				isLoading={isLoadingPresetForm}
				isRenamable
				isWithoutUseForms
				minHeight={minHeight}
				name='preset'
				noOptionsMessage='No presets found'
				onDeleteClick={openDeleteConfirmPreset}
				onEditClick={onEditClickPreset}
				onRenameClick={onRenameClickPreset}
				options={presets}
				placeholder='Select preset'
				renameTooltip='Rename preset'
				required
				singleOpenGroup
				value={presetValue}
			/>
			{isPresetModifiedByUser &&
				<div className='f5gParametersPresetModifiedText'>
					You have changed initial preset parameters
				</div>
			}
		</div>
	);
};

export default Preset;
